import { useEffect, useState } from 'react';
import { getApiErrorMessage } from '@/lib/apiError';

/**
 * Données chargées pour une clé (l'id d'une fiche) : load(key) à l'arrivée, à chaque changement de clé et à chaque
 * reload(). Pendant un rechargement, les données précédentes restent affichées ; pour une autre clé, data repasse à
 * null (loaded faux) jusqu'à la réponse. load doit être stable (fonction du module). Clé null : rien à charger (société
 * pas encore connue…), pas d'appel.
 */
export function useLoader<T>(key: string | null, load: (key: string) => Promise<T>) {
  const [version, setVersion] = useState(0);
  const [state, setState] = useState<{ key: string; data: T | null; error: string | null } | null>(null);

  useEffect(() => {
    if (key === null) return;
    let cancelled = false;
    load(key)
      .then((data) => !cancelled && setState({ key, data, error: null }))
      .catch((e) => !cancelled && setState({ key, data: null, error: getApiErrorMessage(e) }));

    return () => {
      cancelled = true;
    };
  }, [key, version, load]);

  const current = state?.key === key ? state : null;

  return {
    data: current?.data ?? null,
    error: current?.error ?? null,
    loaded: current !== null,
    reload: () => setVersion((v) => v + 1),
  };
}
